import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import HireApplication from '../../../../../../services/HireApplication';
import {
  PreEmploymentFormData,
  EmploymentHistoryData,
  buildEmptyPreEmployment,
  buildEmptyHistory,
  PRE_EMPLOYMENT_STORAGE_KEY,
  PRE_EMPLOYMENT_HISTORY_STORAGE_KEY,
} from './PreEmploymentTypes';

interface SubmitArgs {
  token: string;
  userId?: string;
  userName?: string;
}

interface PreEmploymentDraftState {
  formData: PreEmploymentFormData;
  history: EmploymentHistoryData;
  updateForm: (patch: Partial<PreEmploymentFormData>) => void;
  updateHistory: (next: EmploymentHistoryData) => void;
  saveAsDraft: (args: SubmitArgs) => Promise<void>;
  submitFinal: (args: SubmitArgs) => Promise<void>;
  clear: () => void;
}

const readStorage = <T>(key: string): T | null => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
};

const parseApplicationJson = (raw: any): { formData?: any; employmentHistory?: any } => {
  if (!raw) return {};
  try {
    if (typeof raw === 'string') return JSON.parse(raw);
    return raw;
  } catch {
    return {};
  }
};

const usePreEmploymentDraft = (token: string, applicationDraft?: any): PreEmploymentDraftState => {
  const [formData, setFormData] = useState<PreEmploymentFormData>(buildEmptyPreEmployment);
  const [history, setHistory] = useState<EmploymentHistoryData>(buildEmptyHistory);

  useEffect(() => {
    if (!token) return;
    const stored = readStorage<PreEmploymentFormData>(PRE_EMPLOYMENT_STORAGE_KEY(token));
    const storedHistory = readStorage<EmploymentHistoryData>(PRE_EMPLOYMENT_HISTORY_STORAGE_KEY(token));
    if (stored) {
      setFormData({ ...buildEmptyPreEmployment(), ...stored });
    } else if (applicationDraft) {
      const parsed = parseApplicationJson(applicationDraft.json);
      if (parsed.formData) {
        setFormData({ ...buildEmptyPreEmployment(), ...parsed.formData });
      }
    }
    if (storedHistory) {
      setHistory({ ...buildEmptyHistory(), ...storedHistory });
    } else if (applicationDraft) {
      const parsed = parseApplicationJson(applicationDraft.json);
      if (parsed.employmentHistory) {
        setHistory({ ...buildEmptyHistory(), ...parsed.employmentHistory });
      }
    }
  }, [token, applicationDraft]);

  const updateForm = useCallback(
    (patch: Partial<PreEmploymentFormData>) => {
      setFormData((prev) => {
        const next = { ...prev, ...patch };
        try {
          localStorage.setItem(PRE_EMPLOYMENT_STORAGE_KEY(token), JSON.stringify(next));
        } catch {
          // ignore
        }
        return next;
      });
    },
    [token],
  );

  const updateHistory = useCallback(
    (next: EmploymentHistoryData) => {
      setHistory(next);
      try {
        localStorage.setItem(PRE_EMPLOYMENT_HISTORY_STORAGE_KEY(token), JSON.stringify(next));
      } catch {
        // ignore
      }
    },
    [token],
  );

  const submitWith = useCallback(
    async ({ token: tk, userId, userName }: SubmitArgs, isDraft: boolean) => {
      const submittedForm = isDraft ? formData : { ...formData, date: moment().format('YYYY-MM-DD') };
      await HireApplication('').create({
        type: 'APPLICATION_FORM',
        userId,
        userName,
        userToken: tk,
        json: JSON.stringify({ formData: submittedForm, employmentHistory: history }),
        signature: '',
        isDraft,
      });
    },
    [formData, history],
  );

  const saveAsDraft = useCallback((args: SubmitArgs) => submitWith(args, true), [submitWith]);
  const submitFinal = useCallback((args: SubmitArgs) => submitWith(args, false), [submitWith]);

  const clear = useCallback(() => {
    try {
      localStorage.removeItem(PRE_EMPLOYMENT_STORAGE_KEY(token));
      localStorage.removeItem(PRE_EMPLOYMENT_HISTORY_STORAGE_KEY(token));
    } catch {
      // ignore
    }
    setFormData(buildEmptyPreEmployment());
    setHistory(buildEmptyHistory());
  }, [token]);

  return { formData, history, updateForm, updateHistory, saveAsDraft, submitFinal, clear };
};

export default usePreEmploymentDraft;
